// import GT from '../../Images/GT.jpg'

const styles = {
  wrapper: {
    marginTop: '10px',
    marginLeft: '0px',
    border: '3px',
    padding: '40px',
    borderStyle: 'solid',
    borderColor: 'grey',
    fontFamily: "'Georgia', serif",
    color: '#1a1a2e',
    lineHeight: '1.7',
  },
  h1: {
    fontSize: '2rem',
    fontWeight: '700',
    borderBottom: '3px solid #2E75B6',
    paddingBottom: '8px',
    marginBottom: '24px',
    color: '#1F3864',
  },
  h2: {
    fontSize: '1.3rem',
    fontWeight: '700',
    color: '#2E75B6',
    marginTop: '36px',
    marginBottom: '12px',
    borderLeft: '4px solid #2E75B6',
    paddingLeft: '10px',
  },
  p: {
    marginBottom: '12px',
    fontSize: '0.97rem',
  },
  infoBox: {
    background: '#EBF3FB',
    border: '2px solid #2E75B6',
    borderRadius: '6px',
    padding: '16px 20px',
    margin: '16px 0',
  },
  infoBoxTitle: {
    fontWeight: '700',
    fontFamily: "'Courier New', monospace",
    fontSize: '0.88rem',
    color: '#1F3864',
    marginBottom: '8px',
  },
  infoBoxItem: {
    fontSize: '0.9rem',
    margin: '4px 0',
    paddingLeft: '12px',
    borderLeft: '2px solid #2E75B6',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    marginBottom: '20px',
    fontSize: '0.92rem',
  },
  th: {
    background: '#2E75B6',
    color: '#fff',
    padding: '10px 14px',
    textAlign: 'left',
    fontWeight: '700',
    fontSize: '0.9rem',
  },
  tdEven: {
    padding: '10px 14px',
    borderBottom: '1px solid #ccd9ea',
    verticalAlign: 'top',
    background: '#f5f9ff',
  },
  tdOdd: {
    padding: '10px 14px',
    borderBottom: '1px solid #ccd9ea',
    verticalAlign: 'top',
    background: '#ffffff',
  },
  tdLabel: {
    fontWeight: '600',
    color: '#1F3864',
    whiteSpace: 'nowrap',
    width: '220px',
  },
  code: {
    fontFamily: "'Courier New', monospace",
    fontSize: '0.88rem',
    whiteSpace: 'pre-wrap',
  },
  diagramWrap: {
    margin: '24px 0',
    background: '#f8fbff',
    border: '1px solid #ccd9ea',
    borderRadius: '8px',
    padding: '20px',
  },
  diagramTitle: {
    fontWeight: '700',
    fontSize: '0.95rem',
    color: '#1F3864',
    marginBottom: '12px',
    textAlign: 'center',
    letterSpacing: '0.02em',
  },
  diagramCaption: {
    fontSize: '0.84rem',
    color: '#555',
    textAlign: 'center',
    marginTop: '10px',
    fontStyle: 'italic',
  },
  threeCol: {
    display: 'flex',
    gap: '16px',
    flexWrap: 'wrap',
    justifyContent: 'center',
  },
  colCard: {
    flex: '1',
    minWidth: '200px',
    background: '#fff',
    border: '1px solid #ccd9ea',
    borderRadius: '6px',
    padding: '14px',
    textAlign: 'center',
  },
  colCardTitle: {
    fontWeight: '700',
    fontSize: '0.85rem',
    marginBottom: '8px',
  },
};

function InfoBox({ title, items }) {
  return (
    <div style={styles.infoBox}>
      <div style={styles.infoBoxTitle}>{title}</div>
      {items.map((item, i) => (
        <div key={i} style={styles.infoBoxItem}>{item}</div>
      ))}
    </div>
  );
}

function Table2({ rows }) {
  return (
    <table style={styles.table}>
      <thead>
        <tr>
          <th style={{ ...styles.th, width: '220px' }}>Concept</th>
          <th style={styles.th}>Formula / Notes</th>
        </tr>
      </thead>
      <tbody>
        {rows.map(([label, content], i) => (
          <tr key={i}>
            <td style={{ ...(i % 2 === 0 ? styles.tdEven : styles.tdOdd), ...styles.tdLabel }}>{label}</td>
            <td style={{ ...(i % 2 === 0 ? styles.tdEven : styles.tdOdd), ...styles.code }}>{content}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ─────────────────────────────────────────────────────────────────
// DIAGRAM 1: Polynomial curve with roots marked on x-axis
// ─────────────────────────────────────────────────────────────────
function DiagramRoots() {
  const W = 500, H = 220;
  const ox = 60, oy = 110;
  const scaleX = 60, scaleY = 40;

  const f = x => (x + 2) * (x - 0.5) * (x - 2.5);
  const toSVG = (x, y) => [ox + x * scaleX, oy - y * scaleY];

  const pts = [];
  for (let xi = -2.8; xi <= 3.2; xi += 0.04) {
    const [sx, sy] = toSVG(xi, f(xi));
    pts.push(`${sx},${sy}`);
  }
  const path = 'M ' + pts.join(' L ');

  const roots = [-2, 0.5, 2.5];
  const labelOffsets = [[-22, 16], [6, 16], [6, 16]];

  return (
    <div style={styles.diagramWrap}>
      <div style={styles.diagramTitle}>Factor Theorem — Polynomial Roots on the x-axis</div>
      <div style={{ textAlign: 'center' }}>
        <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} style={{ maxWidth: '100%' }}>
          {[-2,-1,0,1,2,3].map(x => {
            const [sx] = toSVG(x, 0);
            return <line key={x} x1={sx} y1={20} x2={sx} y2={H-20} stroke="#e0e8f0" strokeWidth="1"/>;
          })}
          {[-2,-1,0,1,2].map(y => {
            const [,sy] = toSVG(0, y);
            return <line key={y} x1={20} y1={sy} x2={W-20} y2={sy} stroke="#e0e8f0" strokeWidth="1"/>;
          })}
          <line x1={20} y1={oy} x2={W-10} y2={oy} stroke="#1F3864" strokeWidth="1.5"/>
          <line x1={ox} y1={10} x2={ox} y2={H-10} stroke="#1F3864" strokeWidth="1.5"/>
          <text x={W-8} y={oy+4} fontSize="12" fill="#1F3864" textAnchor="middle">x</text>
          <text x={ox} y={6} fontSize="12" fill="#1F3864" textAnchor="middle">f(x)</text>
          {[-2,-1,1,2,3].map(x => {
            const [sx] = toSVG(x, 0);
            return <text key={x} x={sx} y={oy+14} fontSize="10" fill="#555" textAnchor="middle">{x}</text>;
          })}
          <path d={path} fill="none" stroke="#2E75B6" strokeWidth="2.5" strokeLinejoin="round"/>
          {roots.map((r, i) => {
            const [sx, sy] = toSVG(r, 0);
            const [sx0] = toSVG(r, 0);
            return (
              <g key={i}>
                <line x1={sx0} y1={oy-2} x2={sx0} y2={oy-58} stroke="#e05555" strokeWidth="1" strokeDasharray="4,3"/>
                <circle cx={sx} cy={sy} r={5} fill="#e05555" stroke="#fff" strokeWidth="1.5"/>
                <text x={sx+labelOffsets[i][0]} y={sy+labelOffsets[i][1]} fontSize="11" fill="#e05555" fontWeight="700">r{i+1}={r}</text>
                <text x={sx} y={oy-62} fontSize="10" fill="#888" textAnchor="middle">f(r{i+1})=0</text>
              </g>
            );
          })}
          <text x={ox+3.0*scaleX} y={oy-1.7*scaleY} fontSize="11" fill="#2E75B6" fontWeight="700">
            f(x)=(x+2)(x-½)(x-2.5)
          </text>
        </svg>
      </div>
      <div style={styles.diagramCaption}>
        The curve crosses the x-axis at three roots. At each root f(r)=0, so (x−r) is a factor of f(x).
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// DIAGRAM 2: Synthetic division step-by-step
// ─────────────────────────────────────────────────────────────────
function DiagramSyntheticDivision() {
  const W = 520, H = 210;
  const cols = [130, 230, 330, 430];
  const rowTop = 65, rowMid = 108, rowBot = 155;

  const coeffs = [1, -6, 11, -6];
  const carry =  [null, 1, -5, 6];
  const bottom = [1, -5, 6, 0];

  return (
    <div style={styles.diagramWrap}>
      <div style={styles.diagramTitle}>Synthetic Division — Dividing f(x) = x³ − 6x² + 11x − 6 by (x − 1)</div>
      <div style={{ textAlign: 'center' }}>
        <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} style={{ maxWidth: '100%' }}>
          <defs>
            <marker id="arr" markerWidth="7" markerHeight="7" refX="6" refY="3.5" orient="auto">
              <polygon points="0 0, 7 3.5, 0 7" fill="#e05555"/>
            </marker>
          </defs>

          {/* Root box */}
          <rect x={42} y={rowTop-20} width={36} height={28} rx={4} fill="#2E75B6"/>
          <text x={60} y={rowTop-2} textAnchor="middle" fontSize="15" fill="#fff" fontWeight="700">1</text>
          <text x={60} y={rowTop+18} textAnchor="middle" fontSize="10" fill="#2E75B6">root r</text>

          {/* Divider lines */}
          <line x1={88} y1={rowTop-22} x2={88} y2={rowBot+14} stroke="#1F3864" strokeWidth="2"/>
          <line x1={88} y1={rowMid+22} x2={W-16} y2={rowMid+22} stroke="#1F3864" strokeWidth="1.5"/>

          {/* Column headers */}
          {['x³','x²','x¹','x⁰'].map((lbl, i) => (
            <text key={i} x={cols[i]} y={rowTop-24} textAnchor="middle" fontSize="11" fill="#888">{lbl}</text>
          ))}

          {/* Top row: original coefficients */}
          {coeffs.map((c, i) => (
            <text key={i} x={cols[i]} y={rowTop} textAnchor="middle" fontSize="16" fill="#1F3864" fontWeight="700">{c}</text>
          ))}

          {/* Middle row: multiplied carry values + arrows */}
          {carry.map((c, i) => c !== null && (
            <g key={i}>
              <path
                d={`M ${cols[i-1]+10} ${rowBot-20} Q ${(cols[i-1]+cols[i])/2} ${rowMid+38} ${cols[i]-10} ${rowMid+8}`}
                fill="none" stroke="#e05555" strokeWidth="1.3" strokeDasharray="4,3" markerEnd="url(#arr)"
              />
              <text x={cols[i]} y={rowMid+18} textAnchor="middle" fontSize="14" fill="#e05555">
                {c > 0 ? `+${c}` : c}
              </text>
              <text x={(cols[i-1]+cols[i])/2} y={rowMid-2} textAnchor="middle" fontSize="9" fill="#e05555">×1</text>
            </g>
          ))}

          {/* Bottom row: result boxes */}
          {bottom.map((c, i) => (
            <g key={i}>
              <rect x={cols[i]-24} y={rowBot-6} width={48} height={28} rx={3}
                fill={i===3 ? '#fff3f3' : '#EBF3FB'}
                stroke={i===3 ? '#e05555' : '#2E75B6'} strokeWidth="1.3"/>
              <text x={cols[i]} y={rowBot+12} textAnchor="middle" fontSize="15"
                fill={i===3 ? '#e05555' : '#1F3864'} fontWeight="700">{c}</text>
            </g>
          ))}

          {/* Power labels under result */}
          {['x²','x¹','x⁰','R'].map((lbl, i) => (
            <text key={i} x={cols[i]} y={rowBot+46} textAnchor="middle" fontSize="10"
              fill={i===3 ? '#e05555' : '#2E75B6'}>{lbl}</text>
          ))}

          <text x={260} y={H-4} textAnchor="middle" fontSize="11" fill="#555" fontStyle="italic">
            Result: x² − 5x + 6, remainder 0 → (x−1) is a factor ✓
          </text>
        </svg>
      </div>
      <div style={styles.diagramCaption}>
        Bring down the leading coefficient, multiply by r, add to the next column. Remainder = 0 confirms r is a root.
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// DIAGRAM 3: Three ODE response curves
// ─────────────────────────────────────────────────────────────────
function DiagramResponseCurves() {
  const cases = [
    { label: 'Overdamped', subtitle: 'Two distinct real roots', color: '#2E75B6',  fn: t => Math.exp(-0.5*t) + 0.3*Math.exp(-2*t) },
    { label: 'Critically Damped', subtitle: 'Repeated real root',      color: '#e07b00', fn: t => (1+2*t)*Math.exp(-1.5*t) },
    { label: 'Underdamped',  subtitle: 'Complex conjugate roots',  color: '#c0392b', fn: t => Math.exp(-0.35*t)*Math.cos(2.2*t) },
  ];

  const cardW = 160, cardH = 130;
  const pad = { l:28, r:12, t:18, b:28 };
  const innerW = cardW - pad.l - pad.r;
  const innerH = cardH - pad.t - pad.b;
  const tMax = 5.5, yMin = -0.55, yMax = 1.35;

  const toX = t => pad.l + (t/tMax)*innerW;
  const toY = y => pad.t + innerH - ((y-yMin)/(yMax-yMin))*innerH;

  return (
    <div style={styles.diagramWrap}>
      <div style={styles.diagramTitle}>Characteristic Equation — Three Response Types from Root Location</div>
      <div style={styles.threeCol}>
        {cases.map(({ label, subtitle, color, fn }, ci) => {
          const pts = [];
          for (let ti = 0; ti <= tMax; ti += 0.05) {
            const y = Math.max(yMin, Math.min(yMax, fn(ti)));
            pts.push(`${toX(ti)},${toY(y)}`);
          }
          const path = 'M ' + pts.join(' L ');
          const zeroY = toY(0);

          return (
            <div key={ci} style={styles.colCard}>
              <div style={{ ...styles.colCardTitle, color }}>{label}</div>
              <div style={{ fontSize: '0.78rem', color: '#666', marginBottom: '6px' }}>{subtitle}</div>
              <svg width={cardW} height={cardH} viewBox={`0 0 ${cardW} ${cardH}`} style={{ maxWidth: '100%' }}>
                <line x1={pad.l} y1={zeroY} x2={cardW-pad.r} y2={zeroY} stroke="#ddd" strokeWidth="1"/>
                <line x1={pad.l} y1={pad.t} x2={pad.l} y2={pad.t+innerH} stroke="#aaa" strokeWidth="1.2"/>
                <line x1={pad.l} y1={pad.t+innerH} x2={cardW-pad.r} y2={pad.t+innerH} stroke="#aaa" strokeWidth="1.2"/>
                <text x={pad.l-3} y={pad.t+4} fontSize="9" fill="#aaa" textAnchor="end">y</text>
                <text x={cardW-pad.r+2} y={pad.t+innerH+3} fontSize="9" fill="#aaa">t</text>
                <text x={cardW-pad.r+2} y={zeroY+3} fontSize="8" fill="#bbb">0</text>
                <path d={path} fill="none" stroke={color} strokeWidth="2.3" strokeLinejoin="round"/>
                <circle cx={toX(0)} cy={toY(fn(0))} r={3} fill={color}/>
              </svg>
            </div>
          );
        })}
      </div>
      <div style={styles.diagramCaption}>
        All three systems start from the same initial condition. The type of root alone determines the shape of the response.
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// DIAGRAM 4: Complex plane — stability map
// ─────────────────────────────────────────────────────────────────
function DiagramComplexPlane() {
  const W = 460, H = 265;
  const cx = 200, cy = 132;
  const sx = 70, sy = 55;

  const toSVG = (rx, ry) => [cx + rx*sx, cy - ry*sy];

  const roots = [
    { x:-2,   y:0,    label:'Overdamped',     sub:'real, negative',  color:'#2E75B6',  shape:'circle'  },
    { x:-1.5, y:1.8,  label:'Underdamped',    sub:'α<0, stable',     color:'#c0392b',  shape:'x'       },
    { x:-1.5, y:-1.8, label:'',               sub:'',                color:'#c0392b',  shape:'x'       },
    { x:0,    y:2,    label:'Undamped',        sub:'α=0, marginal',   color:'#e07b00',  shape:'diamond' },
    { x:0,    y:-2,   label:'',               sub:'',                color:'#e07b00',  shape:'diamond' },
    { x:1.5,  y:1.2,  label:'Unstable',       sub:'α>0, grows',      color:'#888',     shape:'x'       },
    { x:1.5,  y:-1.2, label:'',               sub:'',                color:'#888',     shape:'x'       },
  ];

  return (
    <div style={styles.diagramWrap}>
      <div style={styles.diagramTitle}>Complex Plane — Root Location Determines System Stability</div>
      <div style={{ textAlign: 'center' }}>
        <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} style={{ maxWidth: '100%' }}>
          {/* Shading */}
          <rect x={0} y={0} width={cx} height={H} fill="#EBF3FB" opacity="0.55"/>
          <rect x={cx} y={0} width={W-cx} height={H} fill="#fff0f0" opacity="0.55"/>
          <text x={cx/2} y={18} textAnchor="middle" fontSize="12" fill="#2E75B6" fontWeight="700">STABLE</text>
          <text x={cx+(W-cx)/2} y={18} textAnchor="middle" fontSize="12" fill="#c0392b" fontWeight="700">UNSTABLE</text>

          {/* Grid */}
          {[-2,-1,1,2].map(v => {
            const [svgX] = toSVG(v, 0);
            const [,svgY] = toSVG(0, v);
            return (
              <g key={v}>
                <line x1={svgX} y1={10} x2={svgX} y2={H-10} stroke="#ddd" strokeWidth="1"/>
                <line x1={10}   y1={svgY} x2={W-10} y2={svgY} stroke="#ddd" strokeWidth="1"/>
                <text x={svgX} y={cy+12} textAnchor="middle" fontSize="9" fill="#bbb">{v}</text>
                <text x={cx-5}  y={svgY+3} textAnchor="end"  fontSize="9" fill="#bbb">{v}i</text>
              </g>
            );
          })}

          {/* Stability boundary */}
          <line x1={cx} y1={10} x2={cx} y2={H-10} stroke="#1F3864" strokeWidth="2" strokeDasharray="6,3"/>

          {/* Axes */}
          <line x1={10} y1={cy} x2={W-10} y2={cy} stroke="#1F3864" strokeWidth="1.5"/>
          <text x={W-8} y={cy-5} fontSize="11" fill="#1F3864" fontWeight="700">σ (real)</text>
          <text x={cx+4} y={12} fontSize="11" fill="#1F3864" fontWeight="700">jω</text>
          <text x={cx+4} y={cy-4} fontSize="10" fill="#1F3864">0</text>

          {/* Root markers */}
          {roots.map(({ x, y, label, sub, color, shape }, i) => {
            const [rx, ry] = toSVG(x, y);
            const marker = shape === 'circle'
              ? <circle cx={rx} cy={ry} r={7} fill={color} stroke="#fff" strokeWidth="1.5"/>
              : shape === 'diamond'
              ? <polygon points={`${rx},${ry-8} ${rx+7},${ry} ${rx},${ry+8} ${rx-7},${ry}`} fill={color} stroke="#fff" strokeWidth="1.5"/>
              : <g>
                  <line x1={rx-6} y1={ry-6} x2={rx+6} y2={ry+6} stroke={color} strokeWidth="2.5"/>
                  <line x1={rx+6} y1={ry-6} x2={rx-6} y2={ry+6} stroke={color} strokeWidth="2.5"/>
                </g>;
            return (
              <g key={i}>
                {marker}
                {label && <>
                  <text x={rx+10} y={ry-4}  fontSize="10" fill={color} fontWeight="700">{label}</text>
                  <text x={rx+10} y={ry+8}  fontSize="9"  fill="#777">{sub}</text>
                </>}
              </g>
            );
          })}

          {/* Legend */}
          <rect x={312} y={H-75} width={140} height={68} rx={4} fill="#fff" stroke="#ddd"/>
          {[
            { shape:'circle',  color:'#2E75B6', label:'Real root' },
            { shape:'x',       color:'#c0392b', label:'Complex conjugate pair' },
            { shape:'diamond', color:'#e07b00', label:'Purely imaginary (±jω)' },
          ].map(({ shape, color, label }, i) => {
            const lx = 324, ly = H-60+i*19;
            const m = shape==='circle'
              ? <circle cx={lx+5} cy={ly+3} r={5} fill={color}/>
              : shape==='diamond'
              ? <polygon points={`${lx+5},${ly-3} ${lx+10},${ly+3} ${lx+5},${ly+9} ${lx},${ly+3}`} fill={color}/>
              : <g>
                  <line x1={lx}    y1={ly}   x2={lx+10} y2={ly+7} stroke={color} strokeWidth="2"/>
                  <line x1={lx+10} y1={ly}   x2={lx}    y2={ly+7} stroke={color} strokeWidth="2"/>
                </g>;
            return <g key={i}>{m}<text x={lx+15} y={ly+7} fontSize="9" fill="#444">{label}</text></g>;
          })}
        </svg>
      </div>
      <div style={styles.diagramCaption}>
        Left half-plane (negative real part) → stable decay. Right half-plane → unstable growth. Imaginary axis → sustained oscillation.
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// DIAGRAM 5: Newton-Raphson iteration
// ─────────────────────────────────────────────────────────────────
function DiagramNewtonRaphson() {
  const W = 500, H = 225;
  const ox = 70, oy = 145;
  const scaleX = 80, scaleY = 28;

  const f  = x => x*x - 3;
  const fp = x => 2*x;
  const toSVG = (x, y) => [ox + x*scaleX, oy - y*scaleY];

  const iters = [3];
  for (let i = 0; i < 3; i++) {
    const xn = iters[iters.length-1];
    iters.push(xn - f(xn)/fp(xn));
  }

  const pts = [];
  for (let xi = 0.5; xi <= 3.4; xi += 0.04) {
    const [sx, sy] = toSVG(xi, f(xi));
    pts.push(`${sx},${sy}`);
  }
  const curvePath = 'M ' + pts.join(' L ');

  const colors = ['#c0392b','#e07b00','#2E75B6','#27ae60'];

  return (
    <div style={styles.diagramWrap}>
      <div style={styles.diagramTitle}>Newton-Raphson — Iterative Root Finding for f(x) = x² − 3</div>
      <div style={{ textAlign: 'center' }}>
        <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} style={{ maxWidth: '100%' }}>
          {/* Grid */}
          {[1,2,3].map(x => { const [sx] = toSVG(x,0); return <line key={x} x1={sx} y1={20} x2={sx} y2={H-20} stroke="#eee" strokeWidth="1"/>; })}
          {[-2,0,2,4,6].map(y => { const [,sy] = toSVG(0,y); return <line key={y} x1={20} y1={sy} x2={W-10} y2={sy} stroke="#eee" strokeWidth="1"/>; })}

          {/* Axes */}
          <line x1={20} y1={oy} x2={W-10} y2={oy} stroke="#1F3864" strokeWidth="1.5"/>
          <line x1={ox} y1={10} x2={ox}   y2={H-10} stroke="#1F3864" strokeWidth="1.5"/>
          <text x={W-8} y={oy+4}  fontSize="11" fill="#1F3864" textAnchor="middle">x</text>
          <text x={ox}  y={8}     fontSize="11" fill="#1F3864" textAnchor="middle">f(x)</text>

          {/* Tick labels */}
          {[1,2,3].map(x => { const [sx] = toSVG(x,0); return <text key={x} x={sx} y={oy+13} fontSize="9" fill="#888" textAnchor="middle">{x}</text>; })}
          {[-2,2,4,6].map(y => { const [,sy] = toSVG(0,y); return <text key={y} x={ox-6} y={sy+3} fontSize="9" fill="#888" textAnchor="end">{y}</text>; })}

          {/* Curve */}
          <path d={curvePath} fill="none" stroke="#1F3864" strokeWidth="2.2"/>

          {/* NR iterations */}
          {iters.slice(0,3).map((x0, i) => {
            const x1 = iters[i+1];
            const [sx0, sy0] = toSVG(x0, f(x0));
            const [sx1]      = toSVG(x1, 0);
            const [sx0b]     = toSVG(x0, 0);
            const color = colors[i];
            return (
              <g key={i}>
                <line x1={sx0b} y1={oy} x2={sx0} y2={sy0} stroke={color} strokeWidth="1.2" strokeDasharray="4,3"/>
                <line x1={sx0}  y1={sy0} x2={sx1} y2={oy} stroke={color} strokeWidth="1.6"/>
                <text x={sx0b} y={oy+14} fontSize="10" fill={color} textAnchor="middle">x{i}</text>
                <circle cx={sx0} cy={sy0} r={4} fill={color} stroke="#fff" strokeWidth="1.2"/>
              </g>
            );
          })}

          {/* True root */}
          {(() => {
            const [sx] = toSVG(Math.sqrt(3), 0);
            return (
              <g>
                <line x1={sx} y1={oy-42} x2={sx} y2={oy+8} stroke="#27ae60" strokeWidth="1.5" strokeDasharray="4,2"/>
                <circle cx={sx} cy={oy} r={5} fill="#27ae60" stroke="#fff" strokeWidth="1.5"/>
                <text x={sx+6} y={oy-24} fontSize="10" fill="#27ae60" fontWeight="700">x* = √3 ≈ 1.732</text>
                <text x={sx+6} y={oy-10} fontSize="9"  fill="#27ae60">(true root)</text>
              </g>
            );
          })()}

          <text x={W-10} y={28} textAnchor="end" fontSize="11" fill="#555" fontStyle="italic">
            x(n+1) = x(n) − f(x(n)) / f′(x(n))
          </text>
        </svg>
      </div>
      <div style={styles.diagramCaption}>
        Each iteration draws a tangent at the current estimate and finds where it hits zero — the next estimate. Convergence is rapid once close to the root.
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────────
export default function PolynomialEquations(props) {
  return (
    <div style={styles.wrapper}>
      {/* <SectionTitle title={props.topicId}/> */}
      <h1 style={styles.h1}>Polynomial Equations</h1>

      <p style={styles.p}>
       A polynomial equation is an equation involving a variable (usually x) in which an expression is set equal to zero in order to determine its <strong>roots</strong> (zeros). Finding roots is central to mechanical engineering — they appear whenever you solve differential equations for vibration modes, control system stability, or dynamic response. If a polynomial describes the height of a ball over time, finding the roots tells you exactly when it will hit the ground (height = 0).
      </p>

      <InfoBox
        title="General Form:  a_n·xⁿ + a_(n-1)·x^(n-1) + ... + a_1·x + a_0 = 0"
        items={[
          "n = degree (highest exponent). The degree tells you the maximum number of roots.",
          "Coefficients a_n through a_0 are real constants.",
          "A polynomial of degree n has exactly n roots (real or complex, counting multiplicity).",
        ]}
      />

      <h2 style={styles.h2}>Core Rules</h2>

      <DiagramRoots />

      <Table2
        rows={[
          ["Factor Theorem",        "If r is a root of f(x), then (x − r) is a factor of f(x).\nIf (x − r) is a factor, then f(r) = 0.\nUsed to build or verify factored form from known roots."],
          ["Remainder Theorem",     "When f(x) is divided by (x − r), the remainder = f(r).\nIf f(r) = 0, r is a root. Quick way to test candidate roots."],
          ["Rational Root Theorem", "For integer coefficients, any rational root p/q must satisfy:\n  p divides the constant term (a_0)\n  q divides the leading coefficient (a_n)\nGives a finite list of candidates to test."],
          ["Polynomial Long Division","Divides f(x) by (x − r) to produce a lower-degree quotient.\nAfter finding one root, divide it out and reduce the problem."],
          ["Synthetic Division",    "Shorthand version of long division when dividing by (x − r).\nFaster for hand calculation — coefficients only, no variables written."],
          ["Vieta's Formulas (n=2)","For ax² + bx + c = 0 with roots r₁, r₂:\n  r₁ + r₂ = −b/a    (sum of roots)\n  r₁ · r₂ = c/a     (product of roots)"],
          ["Vieta's Formulas (n=3)","For ax³ + bx² + cx + d = 0 with roots r₁, r₂, r₃:\n  r₁+r₂+r₃ = −b/a\n  r₁r₂+r₁r₃+r₂r₃ = c/a\n  r₁·r₂·r₃ = −d/a"],
        ]}
      />

      <DiagramSyntheticDivision />

      <h2 style={styles.h2}>Characteristic Equations — ME Application</h2>
      <p style={styles.p}>
        When solving ODEs, you assume a solution of the form e<sup>rt</sup> and substitute it in. The result is a polynomial in <em>r</em> called the <strong>characteristic equation</strong>. Its roots determine whether the system oscillates, decays, or grows.
      </p>

      <InfoBox
        title="Procedure:  ODE  →  Characteristic Equation  →  Roots  →  Solution Type"
        items={[
          "Step 1: Write the ODE in standard form with constant coefficients.",
          "Step 2: Replace each derivative with the corresponding power of r.  (y'' → r², y' → r, y → 1)",
          "Step 3: Set equal to zero — this is the characteristic polynomial.",
          "Step 4: Find the roots using the quadratic formula, factoring, or numerical methods.",
          "Step 5: Roots determine the solution form (see diagrams below).",
        ]}
      />

      <DiagramResponseCurves />

      <Table2
        rows={[
          ["2nd Order ODE",          "ay'' + by' + cy = 0   →   ar² + br + c = 0"],
          ["Two distinct real roots", "r₁ ≠ r₂ (both real):   y(t) = C₁·e^(r₁t) + C₂·e^(r₂t)\nDecays without oscillation — overdamped."],
          ["Repeated real root",      "r₁ = r₂ = r:   y(t) = (C₁ + C₂t)·e^(rt)\nCritically damped — fastest decay without oscillating."],
          ["Complex conjugate roots", "r = α ± βi:   y(t) = e^(αt)·[C₁·cos(βt) + C₂·sin(βt)]\nα < 0 → decays (underdamped).   α = 0 → sustained oscillation."],
          ["3rd Order ODE",           "ay''' + by'' + cy' + d = 0   →   ar³ + br² + cr + d = 0\nTypically one real root + complex conjugate pair.\nFactor out the real root first to reduce to a quadratic."],
        ]}
      />

      <DiagramComplexPlane />

      <InfoBox
        title="Where Characteristic Equations Appear in Mechanical Engineering"
        items={[
          "Vibrations:  m·x'' + c·x' + k·x = 0  →  mr² + cr + k = 0  →  roots give damped natural frequency",
          "Control Systems:  Transfer function denominators are characteristic polynomials. Root locations in the complex plane determine stability.",
          "Beam Deflection:  4th-order ODE leads to a 4th-degree characteristic equation with 4 roots.",
          "Heat & Mass Transfer:  Exponential decay terms come directly from characteristic equation roots.",
        ]}
      />

      <h2 style={styles.h2}>Numerical Root Finding</h2>
      <p style={styles.p}>
        For polynomials of degree 5 and above, no closed-form formula exists. These methods are standard in engineering software (MATLAB, Python, FEA solvers):
      </p>

      <DiagramNewtonRaphson />

      <Table2
        rows={[
          ["Newton-Raphson",  "x_(n+1) = x_n − f(x_n) / f′(x_n)\nIteratively refines an estimate. Fast convergence near a root. Needs a starting guess."],
          ["Bisection Method","Bracket the root between [a, b] where f(a) and f(b) have opposite signs.\nRepeatedly halve the interval. Slower but guaranteed to converge."],
          ["MATLAB / NumPy",  "roots([a_n, ..., a_1, a_0])  — returns all roots numerically.\nMost practical approach for higher-order engineering problems."],
        ]}
      />
    </div>
  );
}